import { type ReactNode } from 'react';
import { Box, Row, Stack, Chip, Spinner, Text, Button } from '@/components/primitives';
import { Icons, vars } from '@/theme';
import { formMessages } from '@/constants';
import { formatFileSize } from '@/utils/textFormat';
import { testIds } from '@/test/testIds';
import type { FileRef } from '@/types/file';
import {
  useFileRef,
  fileRefProcessingCopy,
  fileRefFailureCopy,
  type FileRefView,
} from './useFileRef';

/**
 * The one component family that renders a `FileRef` (file-handling §14.3.5).
 *
 * Every file-bearing surface goes through this instead of hand-rolling
 * `url ? <img/> : null`, which is what made a processing asset
 * indistinguishable from a deleted one everywhere (§7.1). The four states are
 * always distinguishable, `filename` survives every state, and failure is
 * honest with an optional retry.
 *
 * `variant` picks the presentation, not the semantics: the state machine is
 * identical across all three.
 */
export type FileAttachmentVariant = 'chip' | 'image' | 'thumbnail';

export interface FileAttachmentProps {
  fileRef?: FileRef | null;
  /** S3 key from this session's upload, so the local echo can be shown. */
  localKey?: string | null;
  variant: FileAttachmentVariant;
  /** Rendered when given; a chip gets a delete affordance, images a button. */
  onRemove?: () => void;
  /** Rendered on failure when given — the recovery path is re-upload. */
  onRetry?: () => void;
  /**
   * The LEARNER's view of an ACCEPTED answer file (file-handling plan A1.3):
   * once accepted the file is ours, so the chip never shows failure or a retry
   * — `failed` renders as a neutral "received", and processing copy is
   * type-specific ("checking for malware" for image/document, "processing" for
   * video/audio). Manager surfaces leave this off and see the honest failure.
   */
  learner?: boolean;
  /** Alt text for the image/thumbnail variants; defaults to the filename. */
  alt?: string;
  /** The surface's own empty state, rendered when the file is absent. */
  children?: ReactNode;
  className?: string;
}

/** "photo.png (2.3 MB)", degrading gracefully when size is unknown. */
function labelFor(view: FileRefView): string {
  const name = view.filename ?? formMessages.upload.fileIcon;
  return view.size == null
    ? name
    : formMessages.upload.fileWithSize(name, formatFileSize(view.size));
}

function RetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <Button onClick={onRetry} data-testid={testIds.files.retryButton}>
      {formMessages.upload.retry}
    </Button>
  );
}

function ProcessingOverlay({ view }: { view: FileRefView }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      data-testid={testIds.files.processing}
    >
      <Spinner size={24} />
      <Text variant="caption" color="secondary">
        {formMessages.upload.processing(fileRefProcessingCopy(view.type))}
      </Text>
    </Stack>
  );
}

function FailedState({ view, onRetry }: { view: FileRefView; onRetry?: () => void }) {
  return (
    <Stack alignItems="center" data-testid={testIds.files.failed}>
      <Text variant="caption" color="error">
        {fileRefFailureCopy(view.error)}
      </Text>
      {onRetry ? <RetryButton onRetry={onRetry} /> : null}
    </Stack>
  );
}

/** Learner-facing processing copy: what is actually happening to THIS type. */
function learnerProcessingCopy(type: FileRefView['type']): string {
  return type === 'video' || type === 'audio'
    ? formMessages.upload.learner.processingMedia
    : formMessages.upload.learner.checkingMalware;
}

function ChipVariant({
  view,
  onRemove,
  onRetry,
  learner,
}: {
  view: FileRefView;
  onRemove?: () => void;
  onRetry?: () => void;
  learner?: boolean;
}) {
  if (view.state === 'absent') return null;

  if (learner) {
    // A1.3: accepted is accepted. No failure state, no retry, honest but
    // neutral copy while the server finishes with it.
    const suffix =
      view.state === 'processing'
        ? learnerProcessingCopy(view.type)
        : view.state === 'failed'
          ? formMessages.upload.learner.received
          : null;
    return (
      <Chip
        intent={view.state === 'ready' ? 'default' : 'info'}
        label={suffix ? `${labelFor(view)} — ${suffix}` : labelFor(view)}
        onDelete={onRemove}
        deleteButtonTestId={testIds.files.removeButton}
        data-testid={testIds.files.attachment}
      />
    );
  }

  if (view.state === 'failed') {
    return (
      <Row data-testid={testIds.files.attachment}>
        <Chip
          intent="danger"
          label={`${labelFor(view)} — ${fileRefFailureCopy(view.error)}`}
          onDelete={onRemove}
          deleteButtonTestId={testIds.files.removeButton}
          data-testid={testIds.files.failed}
        />
        {view.downloadUrl ? (
          // A3 row 10: the bytes are published even though we couldn't
          // process them — the reviewer can still get the original.
          <a
            href={view.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testIds.files.download}
          >
            {formMessages.upload.downloadOriginal}
          </a>
        ) : null}
        {onRetry ? <RetryButton onRetry={onRetry} /> : null}
      </Row>
    );
  }

  const processing = view.state === 'processing';
  return (
    <Chip
      intent={processing ? 'info' : 'default'}
      label={
        processing
          ? `${labelFor(view)} ${formMessages.upload.processingSuffix}`
          : labelFor(view)
      }
      onDelete={onRemove}
      deleteButtonTestId={testIds.files.removeButton}
      data-testid={testIds.files.attachment}
    />
  );
}

function ImageVariant({
  view,
  alt,
  onRetry,
  children,
}: {
  view: FileRefView;
  alt?: string;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  if (view.state === 'absent') return <>{children ?? null}</>;
  if (view.state === 'failed') return <FailedState view={view} onRetry={onRetry} />;

  return (
    <Stack data-testid={testIds.files.attachment}>
      {view.url ? (
        <img
          src={view.url}
          alt={alt ?? view.filename ?? ''}
          data-testid={testIds.files.image}
          style={{ maxWidth: '100%' }}
        />
      ) : null}
      {view.state === 'processing' ? <ProcessingOverlay view={view} /> : null}
    </Stack>
  );
}

function ThumbnailVariant({ view, alt }: { view: FileRefView; alt?: string }) {
  if (view.state === 'absent') return null;

  const url = view.thumbnailUrl ?? view.url;

  // A thumbnail slot is small, so the honest states are an icon-sized spinner
  // and an error glyph — but they are still *states*, not an empty slot: this
  // is the difference between "your image is being processed" and "there is no
  // image", which is the whole §7.1 row-5 bug.
  if (!url && view.state === 'processing') {
    const label = formMessages.upload.processing(fileRefProcessingCopy(view.type));
    return (
      <Box data-testid={testIds.files.processing} title={label}>
        <Spinner size={16} aria-label={label} />
      </Box>
    );
  }

  if (view.state === 'failed') {
    const reason = fileRefFailureCopy(view.error);
    return (
      <Box data-testid={testIds.files.failed} title={reason} aria-label={reason}>
        <Icons.error style={{ fontSize: 18, color: vars.color.danger.base }} />
      </Box>
    );
  }

  return (
    <Box data-testid={testIds.files.thumbnail}>
      {url ? (
        <img
          src={url}
          alt={alt ?? view.filename ?? ''}
          data-testid={testIds.files.image}
          style={{ width: 32, height: 32, objectFit: 'cover' }}
        />
      ) : (
        <Box as="span" data-testid={testIds.files.thumbnailIcon}>
          <Icons.file aria-label={formMessages.upload.fileIcon} />
        </Box>
      )}
    </Box>
  );
}

export function FileAttachment({
  fileRef,
  localKey,
  variant,
  onRemove,
  onRetry,
  learner,
  alt,
  children,
  className,
}: FileAttachmentProps) {
  const view = useFileRef(fileRef, { localKey });

  const content =
    variant === 'chip' ? (
      <ChipVariant view={view} onRemove={onRemove} onRetry={onRetry} learner={learner} />
    ) : variant === 'image' ? (
      <ImageVariant view={view} alt={alt} onRetry={onRetry}>
        {children}
      </ImageVariant>
    ) : (
      <ThumbnailVariant view={view} alt={alt} />
    );

  return className ? <Box className={className}>{content}</Box> : content;
}
